import React from "react";
import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { fetchProductById } from "../features/products/productsSlice.js";
import { useDispatch, useSelector } from "react-redux";
import { addItemToCart } from "../features/cart/cartSlice.js";
import {
  Plus,
  Minus,
  ChevronDown,
  ChevronUp,
  TriangleAlert,
} from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const placeholder = new URL("../../assets/placeholder.png", import.meta.url)
  .href;

const KATEGORIE_LABELS = {
  feuerloescher: "Feuerlöscher",
  brandschutz: "Brandschutz",
};

export default function Main() {
  const { id } = useParams();
  const [number, setNumber] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const kategorie = searchParams.get("kategorie");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedItem, status } = useSelector((state) => state.products);

  const handleIncrease = () => setNumber((n) => n + 1);

  const handleDecrease = () => {
    if (number > 1) setNumber((n) => n - 1);
  };

  const HandleAddToCart = () => {
    setLoading(true);
    if (!user) {
      toast.error(
        "Bitte melden Sie sich an, um einen Artikel in den Warenkorb zu legen.",
      );
      navigate("/login", { replace: true });
      return;
    }

    dispatch(
      addItemToCart({
        user_id: user.idUser,
        product: selectedItem,
        menge: number,
      }),
    );
    toast.success(`Wurde zum Warenkorb hinzugefügt`);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    if (id) {
      dispatch(fetchProductById({ idProdukt: id }));
    }
    setLoading(false);
  }, [id, dispatch]);

  if (status === "loading") {
    return (
      <div className="flex justify-center pt-16">
        <div
          className="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-blue-500"
          role="status"
        >
          <span className="sr-only">Lädt...</span>
        </div>
      </div>
    );
  }

  if (!selectedItem) {
    return (
      <div className="flex justify-center pt-16">
        <div
          className="animate-spin inline-block w-12 h-12 border-4 border-gray-300 border-t-red-600 rounded-full"
          role="status"
          aria-label="Lädt..."
        />
      </div>
    );
  }

  const details = Array.isArray(selectedItem.Details)
    ? selectedItem.Details.map((d) => ({
        key: d.code,
        label: d.label,
        value: d.einheit ? `${d.wert} ${d.einheit}` : d.wert,
      }))
    : [];

  // Gewicht kommt aus gewicht_kg und wird direkt nach der Höhe angezeigt.
  const gewicht = Number(selectedItem.gewicht_kg);
  if (gewicht > 0) {
    const gewichtRow = {
      key: "gewicht",
      label: "Gewicht",
      value: `${gewicht.toLocaleString("de-CH", { maximumFractionDigits: 3 })} kg`,
    };
    const hoeheIndex = details.findIndex((d) => d.key === "hoehe");
    details.splice(
      hoeheIndex === -1 ? details.length : hoeheIndex + 1,
      0,
      gewichtRow,
    );
  }

  const hinweisText = selectedItem.hinweis_text?.trim();
  const hasDetails = details.length > 0 || Boolean(hinweisText);

  const kategorieLabel = kategorie
    ? (KATEGORIE_LABELS[kategorie] ?? kategorie)
    : null;

  return (
    <div className="kn-page">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-2 mt-1 h-[2px] bg-gray-300 opacity-80 rounded-full animate-pulse" />
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 px-5 sm:px-8 lg:px-10 py-3.5 text-[13px] text-knapp-ink4 border-b border-knapp-borderSubtle flex-wrap">
        <Link
          to="/produkte"
          className="text-knapp-ink4 hover:text-knapp-accent"
        >
          Produkte
        </Link>
        {kategorieLabel && (
          <>
            <span>/</span>
            <span>{kategorieLabel}</span>
          </>
        )}
        <span>/</span>
        <span className="text-knapp-ink">{selectedItem.Name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,1fr)_minmax(360px,500px)] items-start border-b border-knapp-border">
        {/* Linke Spalte: Bild + Details */}
        <section className="px-5 sm:px-8 lg:px-10 pt-7 pb-12 order-2 lg:order-1">
          <div className="relative w-full max-w-[560px] mx-auto aspect-square bg-white border-2 border-knapp-accent rounded-[14px] flex items-center justify-center p-8 sm:p-12">
            <img
              className="max-h-full max-w-full w-auto object-contain"
              src={selectedItem.Bild || placeholder}
              alt={selectedItem.Name}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = placeholder;
              }}
            />
          </div>

          {hasDetails && (
            <div className="mt-10">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full border-b border-knapp-border kn-legend pb-3.5"
              >
                Weitere Details
                {isOpen ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-12 mt-2">
                    {details.map(({ key, label, value }) => (
                      <div
                        key={key}
                        className="flex justify-between gap-4 py-2.5 border-t border-knapp-border text-[15px]"
                      >
                        <span className="text-knapp-ink3">{label}</span>
                        <span className="font-semibold text-right">
                          {value}
                        </span>
                      </div>
                    ))}
                    {hinweisText && (
                      <span className="sm:col-span-2 w-full text-center text-knapp-accent font-medium my-3">
                        {hinweisText}
                      </span>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </section>

        {/* Rechte Spalte: Produktinfo */}
        <aside className="px-5 sm:px-8 lg:px-10 pt-7 pb-12 lg:sticky lg:top-[88px] lg:self-start lg:border-l border-knapp-borderSubtle bg-white order-1 lg:order-2">
          <h1 className="kn-h2">{selectedItem.Name}</h1>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-knapp-ink3">
              CHF
            </span>
            <span className="text-[34px] font-bold tracking-[-0.02em] text-knapp-accent leading-none">
              {Number(selectedItem.Preis_brutto).toFixed(2)}
            </span>
            {Number(selectedItem.hasWarning) === 1 && (
              <TriangleAlert className="h-6 w-6 text-knapp-accent shrink-0" />
            )}
          </div>

          {selectedItem.Beschreibung && (
            <p className="mt-4 kn-body">{selectedItem.Beschreibung}</p>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
            <div className="kn-qty justify-between">
              <button
                onClick={handleDecrease}
                className="kn-qty-btn w-10 h-[46px] text-xl"
                aria-label="Menge verringern"
              >
                <Minus className="h-5 w-5" />
              </button>
              <span className="w-9 text-center text-base font-semibold">
                {number}
              </span>
              <button
                onClick={handleIncrease}
                className="kn-qty-btn w-10 h-[46px] text-xl"
                aria-label="Menge erhöhen"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={HandleAddToCart}
              className="kn-btn-primary flex-1 py-3.5"
            >
              Zum Warenkorb hinzufügen
            </motion.button>
          </div>
        </aside>
      </div>
    </div>
  );
}
